<template>
  <v-dialog
    :value="value"
    max-width="600px"
    persistent
    scrollable
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar color="primary" dark flat>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>New Chat</v-toolbar-title>
      </v-toolbar>

      <v-tabs v-model="tab" centered>
        <v-tab>Private Chat</v-tab>
        <v-tab>Group Chat</v-tab>
      </v-tabs>

      <v-card-text class="pa-4">
        <v-tabs-items v-model="tab">
          <!-- Private Chat Tab -->
          <v-tab-item>
            <v-autocomplete
              v-model="selectedUser"
              :items="users"
              :loading="loadingUsers"
              :search-input.sync="userSearch"
              item-text="first_name"
              item-value="id"
              label="Select User"
              prepend-icon="mdi-account-search"
              return-object
              outlined
              :error-messages="errors.user_id"
            >
              <template v-slot:selection="{ item }">
                {{ item.first_name }} {{ item.last_name }}
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-avatar>
                  <v-img v-if="item.image_path" :src="mediaUrl + item.image_path" />
                  <v-avatar v-else color="primary">
                    <span class="white--text">{{ item.first_name.charAt(0) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.first_name }} {{ item.last_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.position?.description || 'User' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-tab-item>

          <!-- Group Chat Tab -->
          <v-tab-item>
            <v-text-field
              v-model="groupName"
              label="Group Name *"
              prepend-icon="mdi-account-group"
              outlined
              :error-messages="errors.name"
            />

            <v-autocomplete
              v-model="selectedUsers"
              :items="users"
              :loading="loadingUsers"
              :search-input.sync="userSearch"
              item-text="first_name"
              item-value="id"
              label="Add Members *"
              prepend-icon="mdi-account-multiple-plus"
              multiple
              chips
              small-chips
              deletable-chips
              return-object
              outlined
              :error-messages="errors.user_ids"
            >
              <template v-slot:selection="{ item }">
                <v-chip small close @click:close="removeUser(item)">
                  {{ item.first_name }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-avatar>
                  <v-img v-if="item.image_path" :src="mediaUrl + item.image_path" />
                  <v-avatar v-else color="primary">
                    <span class="white--text">{{ item.first_name.charAt(0) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.first_name }} {{ item.last_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.position?.description || 'User' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-autocomplete
              v-if="showStoreSelection"
              v-model="selectedStore"
              :items="stores"
              :loading="loadingStores"
              item-text="store_location"
              item-value="id"
              label="Store (Optional)"
              prepend-icon="mdi-store"
              outlined
              clearable
              :error-messages="errors.store_id"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="createChat"
          :loading="creating"
          :disabled="!canCreate"
        >
          Create Chat
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NewChatDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      tab: 0,
      selectedUser: null,
      selectedUsers: [],
      selectedStore: null,
      groupName: '',
      users: [],
      stores: [],
      userSearch: null,
      loadingUsers: false,
      loadingStores: false,
      creating: false
    };
  },

  computed: {
    canCreate() {
      if (this.tab === 0) {
        // Private chat
        return this.selectedUser !== null;
      } else {
        // Group chat
        return this.groupName.trim() && this.selectedUsers.length >= 2;
      }
    },

    showStoreSelection() {
      // You can customize this based on user role
      return this.user.role_id !== 1; // Example: show for non-admin users
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.loadUsers();
        if (this.showStoreSelection) {
          this.loadStores();
        }
      } else {
        this.reset();
      }
    },

    userSearch() {
      if (this.userSearch) {
        this.loadUsers();
      }
    }
  },

  methods: {
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get('/users', {
          params: {
            page: 1,
            rowsPerPage: 50,
            search_keyword: this.userSearch || ''
          }
        });
        this.users = response.data.data.filter(u => u.id !== this.user.id);
      } catch (error) {
        console.error('Failed to load users:', error);
        this.$toast.error('Failed to load users');
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadStores() {
      this.loadingStores = true;
      try {
        const response = await axios.get('/stores');
        this.stores = response.data.data;
      } catch (error) {
        console.error('Failed to load stores:', error);
      } finally {
        this.loadingStores = false;
      }
    },

    removeUser(user) {
      this.selectedUsers = this.selectedUsers.filter(u => u.id !== user.id);
    },

    async createChat() {
      this.creating = true;

      try {
        let chat;

        if (this.tab === 0) {
          // Create private chat
          chat = await this.$store.dispatch('chat/createPrivateChat', this.selectedUser.id);
          this.$toast.success('Private chat created successfully');
        } else {
          // Create group chat
          chat = await this.$store.dispatch('chat/createGroupChat', {
            name: this.groupName,
            userIds: this.selectedUsers.map(u => u.id),
            storeId: this.selectedStore
          });
          this.$toast.success('Group chat created successfully');
        }

        this.$emit('chatCreated', chat);
        this.close();
      } catch (error) {
        console.error('Failed to create chat:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to create chat');
      } finally {
        this.creating = false;
      }
    },

    close() {
      this.$emit('input', false);
    },

    reset() {
      this.tab = 0;
      this.selectedUser = null;
      this.selectedUsers = [];
      this.selectedStore = null;
      this.groupName = '';
      this.userSearch = null;
    }
  }
};
</script>

<style scoped>
/* No additional styles needed */
</style>
